import Button from "../../../../Components/Common/Button/Button";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import { DrawerMode } from "./AssignmentDrawer";
import NoData from "./NoData";
import { capitalizeFirst } from "../../../../utils/Common/String";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyInspectorIcon from "Assets/Img/EmptyInspectorIcon";

interface InspectorProject {
  name: string;
  role: string;
  coverDateRange?: string;
}

interface InspectorCard {
  id: string;
  name: string;
  initials: string;
  avatarClass: string;
  meta: string;
  status: string;
  statusLabel: string;
  loadText: string;
  loadClass: string;
  loadWidth: string;
  notesTone: string;
  notes: string;
  vacationNote?: string;
  projects: InspectorProject[];
  focused?: string[];
  hasLeave?: boolean;
}

interface InspectorGridProps {
  inspectors: InspectorCard[];
  loading?: boolean;
  onOpenDrawer: (
    project: null,
    mode: DrawerMode,
    focusInspector?: string,
  ) => void;
}

const SKELETON_COUNT = 4;

const InspectorGrid = ({
  inspectors,
  loading,
  onOpenDrawer,
}: InspectorGridProps) => {
  if (loading) {
    return (
      <div className="inspectorGrid">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <article className="inspCard" key={i}>
            <header className="inspCardHeader">
              <Skeleton width={44} height={44} borderRadius="50%" />
              <div className="inspDetails" style={{ flex: 1 }}>
                <Skeleton width="55%" height={13} style={{ marginBottom: 6 }} />
                <Skeleton width="40%" height={11} />
              </div>
            </header>
            <div className="inspCardBody">
              <Skeleton width="100%" height={10} style={{ marginBottom: 10 }} />
              <Skeleton
                width="100%"
                height={8}
                borderRadius={4}
                style={{ marginBottom: 14 }}
              />
              <Skeleton width="35%" height={10} style={{ marginBottom: 8 }} />
              <Skeleton width="100%" height={11} style={{ marginBottom: 6 }} />
              <Skeleton width="85%" height={11} />
            </div>
            <footer className="inspCardFooter">
              <Skeleton width="48%" height={30} borderRadius={6} />
              <Skeleton width="48%" height={30} borderRadius={6} />
            </footer>
          </article>
        ))}
      </div>
    );
  }

  if (inspectors.length === 0) {
    return (
      <EmptyState
        noBorder
        icon={<EmptyInspectorIcon />}
        title="No Inspectors Found"
        subtitle="There are no inspectors available at the moment. Add inspectors to start assigning them to projects."
      />
    );
  }

  return (
    <div className="inspectorGrid">
      {inspectors.map((inspector) => (
        <article className="inspCard" key={inspector.id}>
          <header className="inspCardHeader">
            <div className={`inspAvatar ${inspector.avatarClass}`.trim()}>
              {inspector.initials}
            </div>

            <div className="inspDetails">
              <p className="inspName">{inspector.name}</p>
              <div className="inspHeaderInfo">
                <p className="inspMeta">
                  {capitalizeFirst(inspector.meta) || " "}
                  {inspector.focused &&
                    ` · ${inspector.focused.map((f) => capitalizeFirst(f)).join(" & ")}`}
                </p>

                <span className={`statusDot ${inspector.status}`.trim()} />
              </div>
              {inspector.statusLabel && (
                <div className="inspStatusChipWrap">
                  <span className={`inspStatusChip ${inspector.status}`.trim()}>
                    <span className="dot" />
                    {inspector.statusLabel === "Active"
                      ? "Available"
                      : inspector.statusLabel}
                  </span>
                </div>
              )}
            </div>
          </header>

          <div className="inspCardBody">
            <div className="loadBar">
              <div className="loadTop">
                <span>Project load</span>
                <strong>{inspector.loadText}</strong>
              </div>
              <div className="loadTrack">
                <div
                  className={`loadFill ${inspector.loadClass}`.trim()}
                  style={{ width: inspector.loadWidth }}
                />
              </div>
            </div>

            <p className="projListTitle">Current Projects</p>
            <div className="projectList">
              {inspector.projects.length > 0 ? (
                inspector.projects.map((project) => (
                  <div
                    className={`listItem ${project.role === "coverage" ? "coverageItem" : ""}`.trim()}
                    key={`${inspector.id}-${project.name}`}
                  >
                    <span
                      className="listDot"
                      style={{
                        background:
                          project.role === "coverage" ? "#7a5c00" : "#14508c",
                      }}
                    />
                    <span className="listName">{project.name}</span>
                    <span className="listRoleWrap">
                      <span className={`listRole ${project.role}`.trim()}>
                        {project.role === "coverage" ? "Coverage" : "Primary"}
                      </span>
                      {project.coverDateRange && (
                        <span className="listCoverDate">
                          {project.coverDateRange}
                        </span>
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <NoData text="No projects assigned" />
              )}
            </div>

            {inspector.notes && (
              <p className={`inspNote ${inspector.notesTone}`.trim()}>
                {inspector.notesTone === "danger" ? "⚠ " : ""}
                {inspector.notes}
              </p>
            )}
            {inspector.vacationNote && (
              <p className="inspNote warn">{inspector.vacationNote}</p>
            )}
          </div>

          <footer className="inspCardFooter">
            <Button
              className="btn btnPrimary btnSm"
              onClick={() => onOpenDrawer(null, "assignProject", inspector.id)}
            >
              Assign Projects
            </Button>
            <Button
              className="btn btnSecondary btnSm"
              onClick={() => onOpenDrawer(null, "vacation", inspector.id)}
            >
              {inspector.status === "vacation" || inspector.hasLeave
                ? "Edit Vacation"
                : "Set Vacation"}
            </Button>
          </footer>
        </article>
      ))}
    </div>
  );
};

export default InspectorGrid;
